import { useMemo } from 'react';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { FundGroupDtoIn } from '@/models/in/FundGroupDtoIn';
import Fund from '@/models/Fund';
import { NameMaps } from './targetingNames';

interface UserRow {
  email: string;
  firstName?: string;
  lastName?: string;
}

/**
 * Names for the ids a flag's rollout and history hold. The three lists are the same URLs the
 * release dialog reads, so react-query serves them from one cache.
 */
const useNameMaps = (enabled = true): { maps: NameMaps; isLoading: boolean } => {
  const { data: funds, isLoading: fundsLoading } = useGetAuthApi<Fund[]>(enabled ? 'funds' : '');
  const { data: users, isLoading: usersLoading } = useGetAuthApi<UserRow[]>(enabled ? 'users' : '');
  const { data: groups, isLoading: groupsLoading } = useGetAuthApi<FundGroupDtoIn[]>(enabled ? 'management/fund-groups' : '');

  const maps = useMemo<NameMaps>(() => {
    const fundById = new Map((funds ?? []).map((f) => [f.id, f.name]));
    const userByEmail = new Map((users ?? []).map((u) => [u.email, [u.firstName, u.lastName].filter(Boolean).join(' ')]));
    const groupById = new Map((groups ?? []).map((g) => [g.id, g.name]));
    return {
      fundName: (id) => fundById.get(id) || `#${id}`,
      userName: (email) => userByEmail.get(email) || email,
      groupName: (id) => groupById.get(id) || `#${id}`,
    };
  }, [funds, users, groups]);

  return { maps, isLoading: fundsLoading || usersLoading || groupsLoading };
};

export default useNameMaps;
